// src/components/chat/SuggestionsStrip.tsx
//
// Horizontal scrollable row of suggestion chips that appears above
// the composer whenever the input is empty. Disappears as soon as
// the user types — at that point they don't need a suggestion, they
// know what they want to ask.
//
// Falls back to a small set of generic starters when no AI-generated
// suggestions are available yet (cold start, before the first guide
// refresh has produced any). This keeps the UI useful immediately.

import React, { useEffect, useRef } from 'react';
import { Box, Chip, Skeleton, Typography } from '@mui/material';
import { AutoAwesome, FiberNew } from '@mui/icons-material';
import { usePromptSuggestions } from '../../hooks/usePromptSuggestions';
import { useT } from '../../i18n/useT';

interface SuggestionsStripProps {
  /** Tap a chip → caller fills composer + focuses. */
  onPick: (text: string) => void;
  /** When false (composer has text), the strip hides itself. */
  visible: boolean;
}

// Generic starter prompts for users who haven't yet had a guide
// refresh (cold start). Replaced as soon as AI-generated ones arrive.
const FALLBACK_SUGGESTIONS: string[] = [
  'What are some things to do near me?',
  'Tell me about this neighborhood',
  'Where should I go for a good meal nearby?',
  'What is interesting about where I am?',
];

export const SuggestionsStrip: React.FC<SuggestionsStripProps> = ({
  onPick,
  visible,
}) => {
  const t = useT();
  const { suggestions, loading, hasUnseen, generatedAt, markSeen } =
    usePromptSuggestions();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Mark seen as soon as the strip is rendered + visible to the user
  // for ~600ms.
  useEffect(() => {
    if (!visible) return;
    if (!hasUnseen) return;
    const t = setTimeout(() => {
      markSeen();
    }, 600);
    return () => clearTimeout(t);
  }, [visible, hasUnseen, markSeen]);

  // Lightweight debug log so it's obvious from the console why a
  // user isn't seeing personalized suggestions yet. Strip itself
  // still renders fallbacks regardless.
  useEffect(() => {
    if (!visible) return;
    if (loading) return;
    if (suggestions.length === 0) {
      console.info(
        '[SuggestionsStrip] No AI-generated suggestions yet — using fallback. ' +
          'Trigger a guide refresh (open Discover or Guide tab, or move location) ' +
          'to populate users/{uid}/promptSuggestions/current.'
      );
    } else {
      console.info(
        `[SuggestionsStrip] ${suggestions.length} AI suggestions, generated_at=${
          generatedAt?.toISOString() || '?'
        }`
      );
    }
  }, [visible, loading, suggestions.length, generatedAt]);

  if (!visible) return null;

  // Decide what to render: AI suggestions if we have them, fallback
  // otherwise. Both pathways look identical to the user.
  const isFallback = !loading && suggestions.length === 0;
  const itemsToRender =
    suggestions.length > 0 ? suggestions : [t("What are some things to do near me?"), t("Tell me about this neighborhood"), t("Where should I go for a good meal nearby?"), t("What is interesting about where I am?")];

  // Shared frosted glass styling — matches the AppShell bars so
  // the strip reads as part of the floating chrome rather than
  // letting message content underneath bleed through and become
  // unreadable. We use an inline `style` for the critical CSS to
  // bypass any class-specificity quirks (same pattern as AppShell).
  const frostedStyle: React.CSSProperties = {
    backgroundColor: 'rgba(240, 245, 250, 0.65)',
    backdropFilter: 'saturate(200%) blur(30px)',
    WebkitBackdropFilter: 'saturate(200%) blur(30px)',
  };
  const frostedSx = {
    borderRadius: 3,
    border: '1px solid rgba(0, 0, 0, 0.08)',
    px: 1.5,
    py: 1,
  };

  // While the very first snapshot is loading, show skeletons so
  // the strip doesn't pop in awkwardly when data arrives.
  if (loading && suggestions.length === 0) {
    return (
      <Box
        style={frostedStyle}
        sx={{
          mx: 2,
          mb: 1,
          display: 'flex',
          gap: 1,
          overflowX: 'hidden',
          flexShrink: 0,
          ...frostedSx,
        }}
      >
        {[1, 2, 3, 4].map((i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={32}
            width={140 + ((i * 30) % 80)}
            sx={{ borderRadius: 16, flexShrink: 0 }}
          />
        ))}
      </Box>
    );
  }

  const handlePick = (text: string) => {
    markSeen();
    onPick(text);
  };

  return (
    <Box
      style={frostedStyle}
      sx={{ mx: 2, mb: 1, flexShrink: 0, ...frostedSx }}
    >
      {/* Tiny header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 0.75,
          px: 0.25,
        }}
      >
        <AutoAwesome
          sx={{ fontSize: 12, color: 'text.secondary', opacity: 0.6 }}
        />
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.68rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'text.secondary',
            opacity: 0.7,
          }}
        >
          {isFallback ? t('Try asking') : t('Try asking')}
        </Typography>
        {hasUnseen && !isFallback && (
          <Box
            sx={{
              ml: 0.5,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.25,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontSize: '0.6rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              px: 0.75,
              py: 0.125,
              borderRadius: 1,
              lineHeight: 1.4,
            }}
          >
            <FiberNew sx={{ fontSize: 10 }} />
            
            {t("New")}
          </Box>
        )}
      </Box>

      {/* Horizontal scroller */}
      <Box
        ref={scrollRef}
        onScroll={() => {
          if (hasUnseen) markSeen();
        }}
        sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          flexWrap: 'nowrap',
          py: 0.5,
          px: 0.25,
        }}
      >
        {itemsToRender.map((s, i) => (
          <Chip
            key={`${i}_${s}`}
            label={s}
            onClick={() => handlePick(s)}
            variant="outlined"
            sx={{
              flexShrink: 0,
              maxWidth: 280,
              fontSize: '0.85rem',
              cursor: 'pointer',
              '& .MuiChip-label': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
              '&:hover': {
                bgcolor: 'action.hover',
                borderColor: 'primary.main',
              },
              transition: 'border-color 0.15s ease',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};
